import React, {useEffect, useState} from "react";

import "../css/about.css";
import {useDataStatus} from "../hooks/useJsonLoader";
import {DataStatus} from "../types/Types";
import PageFooter from "../components/PageFooter";

const About: React.FC = () => {
  const fetchDataStatus = useDataStatus();
  const [dataStatus, setDataStatus] = useState<DataStatus | undefined>();

  useEffect(() => {
    fetchDataStatus({}).then(setDataStatus);
  }, [fetchDataStatus]);

  return (
    <div id="about-page" className="bg-white overflow-auto">
      <div className="container py-4">
        <div>
          <h1>
            <span className="fw-semibold">TheCellMap.org</span>{'\t'}
            <span className="fs-6 fw-light">
              storing and visualizing genetic interactions for the budding yeast, <em>S. cerevisiae</em>, and the human haploid cell line, HAP1
            </span>
          </h1>
          <p className="text-justify">
            Providing access to quantitative genomic data is key to ensure large-scale data validation and promote new
            discoveries. TheCellMap.org serves as the central repository for quantitative genetic interaction data for
            [1] <em>Saccharomyces cerevisiae</em> genes produced by systematic Synthetic Genetic Array (SGA) experiments
            and [2] human genes by genome-scale CRISPR-Cas9 screens in the human haploid cell line, HAP1.
            TheCellMap.org provides a set of fundamental tools for analyzing genetic interaction data. In particular, we
            allow users to easily visualize, explore and functionally annotate genetic interactions as well as extract
            and
            re-organize sub-networks using data-driven or annotation-driven network layouts in an intuitive and
            interactive
            manner.
          </p>
        </div>
        <div>
          <h4 className="fw-semibold pt-2">For more information on:</h4>
          <h5 className="fw-semibold pt-2">TheCellMap – Yeast</h5>
          <p>
            <a href="https://github.com/BooneAndrewsLab/thecellmap" target="_blank" rel="noreferrer">
              <img src="https://img.shields.io/badge/GitHub-thecellmap-green?logo=github" alt="GitHub Repo"/>
            </a>
          </p>
          <dl>
            <dt>
              <a href="http://www.g3journal.org/content/early/2017/03/20/g3.117.040220" target="_blank" rel="noreferrer"
                 className="text-decoration-none">
                TheCellMap.org: A Web-Accessible Database for Visualizing and Mining the Global Yeast
                Genetic Interaction Network
              </a>
            </dt>
            <dd>Usaj M, Tan Y, Wang W, VanderSluis B, Zou A, Myers CL, Costanzo M, Andrews B,
              Boone C.
              <br/>
              <em>G3 (Bethesda)</em>. 2017 May 5;7(5):1539-1549. doi: 10.1534/g3.117.040220. PubMed PMID:
              28325812; PubMed
              Central PMCID: PMC5427489.
            </dd>
            <dt>
              <a href="http://science.sciencemag.org/content/353/6306/aaf1420.long" target="_blank" rel="noreferrer"
                 className="text-decoration-none">
                A global genetic interaction network maps a wiring diagram of cellular function
              </a>
            </dt>
            <dd>Costanzo M, VanderSluis B, Koch EN, Baryshnikova A, Pons C, Tan G, Wang W,
              Usaj M, Hanchard J, Lee SD, Pelechano V, Styles EB, Billmann M, van Leeuwen J,
              van Dyk N, Lin ZY, Kuzmin E, Nelson J, Piotrowski JS, Srikumar T, Bahr S, Chen Y,
              Deshpande R, Kurat CF, Li SC, Li Z, Usaj MM, Okada H, Pascoe N, San Luis BJ,
              Sharifpoor S, Shuteriqi E, Simpkins SW, Snider J, Suresh HG, Tan Y, Zhu H,
              Malod-Dognin N, Janjic V, Przulj N, Troyanskaya OG, Stagljar I, Xia T, Ohya Y,
              Gingras AC, Raught B, Boutros M, Steinmetz LM, Moore CL, Rosebrock AP, Caudy AA,
              Myers CL, Andrews B, Boone C.
              <br/>
              <em>Science</em>. 2016 Sep 23;353(6306). pii: aaf1420.
              PubMed PMID: 27708008.
            </dd>
            <dt>
              <a href="http://science.sciencemag.org/content/354/6312/aag0839.long" target="_blank" rel="noreferrer"
                 className="text-decoration-none">
                Exploring genetic suppression interactions on a global scale
              </a>
            </dt>
            <dd>van Leeuwen J, Pons C, Mellor JC, Yamaguchi TN, Friesen H, Koschwanez J, Ušaj
              MM, Pechlaner M, Takar M, Ušaj M, VanderSluis B, Andrusiak K, Bansal P,
              Baryshnikova A, Boone CE, Cao J, Cote A, Gebbia M, Horecka G, Horecka I, Kuzmin
              E, Legro N, Liang W, van Lieshout N, McNee M, San Luis BJ, Shaeri F, Shuteriqi E,
              Sun S, Yang L, Youn JY, Yuen M, Costanzo M, Gingras AC, Aloy P, Oostenbrink C,
              Murray A, Graham TR, Myers CL, Andrews BJ, Roth FP, Boone C.
              <br/>
              <em>Science</em>. 2016 Nov 4;354(6312). pii:
              aag0839. PubMed PMID: 27811238.
            </dd>
          </dl>
        </div>
        <div>
          <h5 className="fw-semibold pt-2">TheCellMap – Human</h5>
          <div>
            <div className="mb-2 fst-italic">
              Source code will be available upon publication
            </div>
            <div>
              <a href="https://github.com/BooneAndrewsLab/thecellmap_human" target="_blank" rel="noreferrer">
                <img src="https://img.shields.io/badge/GitHub-thecellmap_human-green?logo=github" alt="GitHub Repo"/>
              </a>
            </div>
            <div>
              <a href="https://github.com/BooneAndrewsLab/thecellmap_human_backend" target="_blank" rel="noreferrer">
                <img src="https://img.shields.io/badge/GitHub-thecellmap_human_backend-green?logo=github"
                     alt="GitHub Repo"/>
              </a>
            </div>
          </div>
          <dl>
            <dt>
              <a href="https://www.biorxiv.org/content/10.64898/2026.03.16.712129v1" target="_blank" rel="noreferrer"
                 className="text-decoration-none">
                Expanding TheCellMap.org to visualize a genome-scale genetic interaction network for a human cell line
              </a>
            </dt>
            <dd>Ira Horecka, Matej Ušaj, Myra P.D. Masinas, Henry N. Ward, Xiang Zhang, Arshia Z. Hassan, Maximilian
              Billmann, Hannes L. Röst, Chad L. Myers, Michael Costanzo, Brenda Andrews, Charles Boone
              <br/>
              <em>bioRxiv</em> 2026.03.16.712129; doi: https://doi.org/10.64898/2026.03.16.712129
            </dd>
            <dt>
              <a href="https://www.sciencedirect.com/science/article/pii/S0092867426003454" target="_blank"
                 rel="noreferrer"
                 className="text-decoration-none">
                Global genetic interaction network of a human cell maps conserved principles and informs functional
                interpretation of gene co-essentiality profiles
              </a>
            </dt>
            <dd>Maximilian Billmann, Michael Costanzo, Xiang Zhang, Arshia Z Hassan, Mahfuzur Rahman, Kevin R Brown,
              Katherine S Chan, Amy Hin Yan Tong, Carles Pons, Henry N Ward, Catherine Ross, Jolanda van Leeuwen,
              Michael Aregger, Keith A Lawson, Barbara Mair, Amy F Roth, Nesli E Sen, Duncan Forster, Guihong Tan,
              Patricia Mero, Sanna N Masud, Yoonkyu Lee, Magali Aguilera-Uribe, Matej Usaj, Sylvia MT Almeida,
              Kamaldeep Aulakh, Urvi Bhojoo, Saba Birkadze, Nathaniel Budijono, Xunhui Cai, Joseph J Caumanns,
              Megha Chandrashekhar, Daniel Chang, Ryan Climie, Kuheli Dasgupta, Adrian Drazic, Jose I Rojas Echenique,
              Rafael Gacesa, Adrian Granda Farias, Andrea Habsid, Ira Horecka, Kristin Kantautas, Fenghu Ji, Dae-Kyum
              Kim, Seon Yong Lee, Wendy Liang, Julianne Lim, Kevin Lin, Xueibing Lu, Babak Nami, Allison Nixon,
              Nicholas Mikolajewicz, Lyudmila Nedyalkova, Thomas Rohde, Maria Sartori Rodrigues, Martin Soste,
              Eric Schultz, Wen Wang, Ashwin Seetharaman, Emira Shuteriqi, Olga Sizova, David Thomson Taylor,
              Maria Tereshchenko, David Tieu, Jacob Turowec, Tajinder Ubhi, Sylvia Varland, Kyle E Wang, Zi Yang Wang,
              Jiarun Wei, Yu-Xi Xiao, Grant W Brown, Benjamin F Cravatt, Scott J Dixon, Haley DM Wyatt, Hannes L Röst,
              Frederick P Roth, Tian Xia, Gary D Bader, Robbie Loewith, Nicholas G Davis, Brenda Andrews, Chad L Myers,
              Jason Moffat, Charles Boone.
              <br/>
              <em>Cell</em>. 2026.
              <br/>doi: <a href="https://doi.org/10.1016/j.cell.2026.03.044" target="_blank" rel="noreferrer"
                           className="text-decoration-none text-dark">https://doi.org/10.1016/j.cell.2026.03.044</a>
            </dd>
            <dt>
              <a href="https://doi.org/10.1101/2025.06.30.662330 " target="_blank" rel="noreferrer"
                 className="text-decoration-none">
                Quantitative analysis of genetic interactions in human cells from genome-wide CRISPR-Cas9 screens
              </a>
            </dt>
            <dd>Maximilian Billmann, Michael Costanzo, Mahfuzur Rahman, Katherine Chan, Amy Hin Yan Tong, Henry N. Ward,
              Arshia Z. Hassan, Xiang Zhang, Kevin R. Brown, Thomas Rohde, Angela H. Shaw, Catherine Ross,
              Jolanda van Leeuwen, Michael Aregger, Keith Lawson, Barbara Mair, Patricia Mero, Matej Usaj, Brenda
              Andrews,
              Charles Boone, Jason Moffat, Chad L. Myers.
              <br/>
              <em>bioRxiv [Preprint]</em>. 2025 Jun 30.
              <br/>doi: <a href="https://doi.org/10.1101/2025.06.30.662330" target="_blank" rel="noreferrer"
                           className="text-decoration-none text-dark">https://doi.org/10.1101/2025.06.30.662330</a>
            </dd>
          </dl>
        </div>
        <div>
          <h4 className="fw-semibold pt-2">Developed at the University of Toronto and the University of Minnesota
            by:</h4>
          <ul>
            <li><a href="mailto:michael.costanzo@utoronto.ca" target="_blank" rel="noreferrer"
                   className="text-decoration-none">
              Michael Costanzo</a> (Yeast and Human Genetic Interaction projects &amp; data)
            </li>
            <li><a href="mailto:m.usaj@utoronto.ca" target="_blank" rel="noreferrer"
                   className="text-decoration-none">
              Matej Ušaj</a> (Yeast and Human CellMap technical questions)
            </li>
            <li><a href="mailto:ira.horecka@mail.utoronto.ca" target="_blank" rel="noreferrer"
                   className="text-decoration-none">
              Ira Horecka</a> (Human CellMap technical questions)
            </li>
            <li><a href="mailto:myra.masinas@utoronto.ca" target="_blank" rel="noreferrer"
                   className="text-decoration-none">
              Myra Paz Masinas</a> (Yeast and Human CellMap technical questions)
            </li>
            <li>Yizhao Tan (Yeast CellMap)</li>
            <li>Benjamin VanderSluis (Yeast CellMap)</li>
            <li>Wen Wang (Yeast CellMap)</li>
            <li>Albert Zou (Yeast CellMap)</li>
            <li>Arshia Z. Hassan (Human CellMap)</li>
            <li>Xiang Zhang (Human CellMap)</li>
            <li>Chad L Myers (Yeast and Human CellMap)</li>
            <li>Brenda Andrews (Yeast and Human CellMap)</li>
            <li>Charles Boone (Yeast and Human CellMap)</li>
          </ul>
        </div>
        {dataStatus && (
          <div>
            <h4 className="fw-semibold pt-2">External data sources – TheCellMap Human</h4>
            <table className="table table-sm table-borderless w-auto small">
              <thead>
                <tr>
                  <th className="text-muted fw-normal">Source</th>
                  <th className="text-muted fw-normal ps-4">Last updated</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>NCBI Gene Info</td>
                  <td className="ps-4 font-monospace">{dataStatus.geneinfo.split("T")[0]}</td>
                </tr>
                <tr>
                  <td>UniProt</td>
                  <td className="ps-4 font-monospace">{dataStatus.uniprot.split("T")[0]}</td>
                </tr>
                <tr>
                  <td>HGNC</td>
                  <td className="ps-4 font-monospace">{dataStatus.hgnc.split("T")[0]}</td>
                </tr>
                {dataStatus.annotations.map(a => (
                  <tr key={a.alias}>
                    <td>{a.name}</td>
                    <td className="ps-4 font-monospace">{a.date.split("T")[0]}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <div>
          <h4 className="fw-semibold pt-2">Thanks to the following libraries and their developers:</h4>
          <h5 className="fw-semibold pt-2">TheCellMap – Yeast</h5>
          <div className="d-flex flex-wrap gap-2 small text-muted mt-3 pb-3">
            <a href="https://www.djangoproject.com/"
               className="px-3 py-1 rounded-pill border text-decoration-none text-muted bg-light" target="_blank"
               rel="noreferrer">Django</a>
            <a href="https://jquery.com/"
               className="px-3 py-1 rounded-pill border text-decoration-none text-muted bg-light" target="_blank"
               rel="noreferrer">jQuery</a>
            <a href="https://getbootstrap.com/"
               className="px-3 py-1 rounded-pill border text-decoration-none text-muted bg-light" target="_blank"
               rel="noreferrer">Bootstrap</a>
            <a href="https://glyphicons.com/"
               className="px-3 py-1 rounded-pill border text-decoration-none text-muted bg-light" target="_blank"
               rel="noreferrer">Glyphicons PRO</a>
            <a href="https://www.sigmajs.org/"
               className="px-3 py-1 rounded-pill border text-decoration-none text-muted bg-light" target="_blank"
               rel="noreferrer">Sigma.js</a>
            <a href="https://select2.org/"
               className="px-3 py-1 rounded-pill border text-decoration-none text-muted bg-light" target="_blank"
               rel="noreferrer">select2</a>
            <a href="https://bgrins.github.io/Pick-a-Color/"
               className="px-3 py-1 rounded-pill border text-decoration-none text-muted bg-light" target="_blank"
               rel="noreferrer">Pick-a-Color</a>
            <a href="https://medialize.github.io/jQuery-contextMenu/"
               className="px-3 py-1 rounded-pill border text-decoration-none text-muted bg-light" target="_blank"
               rel="noreferrer">jQuery-contextMenu</a>
            <a href="https://github.com/rogeraabbccdd/jquery.fullscreen"
               className="px-3 py-1 rounded-pill border text-decoration-none text-muted bg-light" target="_blank"
               rel="noreferrer">fullscreen</a>
            <a href="https://github.com/eligrey/FileSaver.js"
               className="px-3 py-1 rounded-pill border text-decoration-none text-muted bg-light" target="_blank"
               rel="noreferrer">FileSaver.js</a>
            <a href="https://github.com/eligrey/Blob.js"
               className="px-3 py-1 rounded-pill border text-decoration-none text-muted bg-light" target="_blank"
               rel="noreferrer">Blob.js</a>
            <a href="https://github.com/eligrey/canvas-toBlob.js"
               className="px-3 py-1 rounded-pill border text-decoration-none text-muted bg-light" target="_blank"
               rel="noreferrer">canvas-toBlob.js</a>
            <a href="https://github.com/jindw/xmldom"
               className="px-3 py-1 rounded-pill border text-decoration-none text-muted bg-light" target="_blank"
               rel="noreferrer">XMLWriter</a>
            <a href="https://refreshless.com/nouislider/"
               className="px-3 py-1 rounded-pill border text-decoration-none text-muted bg-light" target="_blank"
               rel="noreferrer">$.noUiSlider</a>
          </div>
          <h5 className="fw-semibold pt-2">TheCellMap – Human</h5>
          <div className="d-flex flex-wrap gap-2 small text-muted mt-3">
            <a href="https://www.djangoproject.com/"
               className="px-3 py-1 rounded-pill border text-decoration-none text-muted bg-light" target="_blank"
               rel="noreferrer">Django</a>
            <a href="https://react.dev/"
               className="px-3 py-1 rounded-pill border text-decoration-none text-muted bg-light" target="_blank"
               rel="noreferrer">React</a>
            <a href="https://mui.com/"
               className="px-3 py-1 rounded-pill border text-decoration-none text-muted bg-light" target="_blank"
               rel="noreferrer">Material UI</a>
            <a href="https://getbootstrap.com/"
               className="px-3 py-1 rounded-pill border text-decoration-none text-muted bg-light" target="_blank"
               rel="noreferrer">Bootstrap</a>
            <a href="https://icons.getbootstrap.com/"
               className="px-3 py-1 rounded-pill border text-decoration-none text-muted bg-light" target="_blank"
               rel="noreferrer">Bootstrap Icons</a>
            <a href="https://emotion.sh/"
               className="px-3 py-1 rounded-pill border text-decoration-none text-muted bg-light" target="_blank"
               rel="noreferrer">Emotion</a>
            <a href="https://www.sigmajs.org/"
               className="px-3 py-1 rounded-pill border text-decoration-none text-muted bg-light" target="_blank"
               rel="noreferrer">Sigma.js</a>
            <a href="https://graphology.github.io/"
               className="px-3 py-1 rounded-pill border text-decoration-none text-muted bg-light" target="_blank"
               rel="noreferrer">Graphology</a>
            <a href="https://lodash.com/"
               className="px-3 py-1 rounded-pill border text-decoration-none text-muted bg-light" target="_blank"
               rel="noreferrer">Lodash</a>
            <a href="https://github.com/eligrey/FileSaver.js"
               className="px-3 py-1 rounded-pill border text-decoration-none text-muted bg-light" target="_blank"
               rel="noreferrer">FileSaver.js</a>
            <a href="https://stuk.github.io/jszip/"
               className="px-3 py-1 rounded-pill border text-decoration-none text-muted bg-light" target="_blank"
               rel="noreferrer">JSZip</a>
            <a href="https://html2canvas.hertzen.com/"
               className="px-3 py-1 rounded-pill border text-decoration-none text-muted bg-light" target="_blank"
               rel="noreferrer">html2canvas</a>
            <a href="https://reactrouter.com/"
               className="px-3 py-1 rounded-pill border text-decoration-none text-muted bg-light" target="_blank"
               rel="noreferrer">React Router</a>
            <a href="https://react-select.com/"
               className="px-3 py-1 rounded-pill border text-decoration-none text-muted bg-light" target="_blank"
               rel="noreferrer">React Select</a>
          </div>
        </div>
      </div>
      <PageFooter/>
    </div>
  );
};

export default About;